/**
 * English — every string this portal says.
 *
 * The staff vocabulary is taken WORD FOR WORD from `pms-web` and `pms-housekeeping`
 * wherever they already had one: „Moj urnik“, „Potrjujem“, „Prihod“, „Presežek — čaka
 * razvrstitev“. That is deliberate rather than lazy, and it is the same argument
 * `HousekeepingVocabulary` makes on the service side — the telephone, the letter and
 * this portal must not call one thing by three names, and a person who reads „Potrjujem“
 * on their supervisor's screen and something else on their own has two features as far
 * as they are concerned.
 *
 * What is new here is what this portal has that no other does: the home screen, the
 * personnel card as its owner reads it, „ostani prijavljen“, and the two sentences that
 * say what this portal deliberately cannot do — swap a shift, and request any absence but
 * leave (docs/OSEBJE.md §10, §18).
 */
export default {
    app: {
        name: "Staff",
    },
    errors: {
        conflict: "The record already exists or is taken",
        generic_error: "An error occurred",
        invalid_data: "Invalid data",
        module_not_licensed: "This module is not enabled for this hotel",
        not_found: "Not found",
        on_add: "Error while adding",
        on_edit: "Error while updating",
        on_load: "Error while loading",
        operation_in_progress: "This stamp is already being recorded. Do not press again — it will appear in a moment.",
        operation_mismatch: "The screen is out of date. Reload it and try again.",
        session_expired: "The session has expired. Sign in again — nothing was done.",
        staff_break_recording_off: "This house does not record breaks.",
        staff_not_employed: "You have no personnel card in force for that day, so the stamp cannot be recorded. Tell your manager or HR.",
        staff_time_busy: "Your stamp is being recorded. Try again in a moment.",
        staff_time_month_closed: "This month has been signed off and can no longer be changed. If something is wrong, tell your manager — only HR can reopen it.",
        staff_time_out_of_range: "That moment is outside the window the record accepts.",
        staff_time_sequence: "That stamp cannot follow the day as it stands (an arrival on a day that is already open, for instance).",
        unauthorized: "You are not allowed to do that",
    },
    general: {
        hint: 'Hint',
        cancel: "Cancel",
        hide_password: "Hide password",
        save: "Save",
        show_password: "Show password",
    },
    home: {
        needs_you: "Waiting for your answer",
        no_shift_today: "No shift is published for today.",
        open_clock: "All stamps",
        waiting_on_others: "Waiting for the house",
    },
    license: {
        contact: "Contact your system administrator to have it switched on. The hotel’s data is untouched.",
        lead: "The staff and working-time module is not part of this hotel's subscription, so this portal has nothing to show.",
        switch_account: "Sign in with another account",
        title: "This service is not enabled",
    },
    login: {
        demo_signing_in: "Signing in …",
        demo_failed: "This server has no such role.",
        failed: "Wrong username or password",
        password: "Password",
        remember: "Stay signed in",
        remember_hint: "On your own telephone. On somebody else's, leave it unticked.",
        same_account: "Sign in with the same username and password as the PMS. A password cannot be reset here — ask your administrator.",
        submit: "Sign in",
        subtitle: "My clock, my rota, my leave and my documents.",
        title: "Sign in",
        username: "Username",
    },
    nav: {
        account: "Account",
        clock: "Clock",
        documents: "Documents",
        home: "Home",
        language: "Language",
        language_de: "Deutsch",
        language_en: "English",
        language_hr: "Hrvatski",
        language_hu: "Magyar",
        language_it: "Italiano",
        language_sl: "Slovenščina",
        leave: "Leave",
        logout: "Log out",
        profile: "My profile",
        rota: "Rota",
        theme: "Appearance",
        theme_dark: "Dark",
        theme_light: "Light",
        theme_system: "System",
    },
    notifications: {
        channel_email: "E-mail",
        channel_email_hint: "Needs your e-mail address.",
        channel_messaging: "Messaging apps",
        channel_messaging_hint: "Needs your WhatsApp number, or Viber paired to you.",
        channel_push: "Phone app",
        channel_push_hint: "Needs a work telephone or watch paired to you.",
        channel_sms: "SMS",
        channel_sms_hint: "Needs your telephone number.",
    },
    profile: {
        account_hint: "Who you are in the system.",
        account_managed_elsewhere: "Your name, username and password are maintained by the system administrator and cannot be changed here.",
        account_title: "Account",
        card_hint: "What the hotel has recorded about your work.",
        card_read_only: "The card is maintained by HR. If something is wrong, tell them — a change is a new card, never an edited old one.",
        card_title: "My personnel card",
        channel_unavailable: "The house has not set this channel up.",
        channels: "Channels",
        channels_hint: "Nothing chosen means every channel the house has. A grey one is a channel the house has not set up.",
        contact_hint: "Where the house should send a rota notice. These are yours and you edit them yourself.",
        contact_title: "My notification details",
        department: "Department",
        email: "E-mail",
        email_hint: "Where a published rota and a changed shift are announced.",
        employee_no: "Employee number",
        engagement: "Legal basis",
        hours_per_week: "{hours} hours a week",
        language_hint: "Applies to this device. The language on your account is set by HR and decides which language the portal opens in the first time.",
        manager: "Manager",
        name: "Name",
        no_card: "There is no card in force today.",
        no_card_hint: "That is normal on a first day or between seasons. If you are working, tell HR.",
        period: "Period",
        period_from: "from {date}",
        phone: "Telephone",
        phone_hint: "International form: +38641123456.",
        phone_invalid: "The telephone must read +386… (8 to 15 digits).",
        position: "Position",
        property: "Property",
        saved: "Saved.",
        time_record: "Working-time record",
        time_record_not_required: "Not required",
        username: "Username",
        weekly_obligation: "Weekly obligation",
        work_pattern: "Spread across the week",
        working_time: "Working time",
    },
    staff: {
        acknowledge: "I confirm",
        acknowledge_failed: "The confirmation could not be recorded.",
        acknowledge_hint: "Acknowledging records that you have seen the change, and when. Until you do, the scheduler is told that you do not know yet.",
        acknowledged: "Receipt recorded",
        basis_agency_worker: "Agency worker",
        basis_copyright_contract: "Copyright contract",
        basis_employment_contract: "Contract of employment",
        basis_other: "Other legal ground",
        basis_pensioner_temporary: "Pensioner temporary work",
        basis_self_employed: "Self-employed contractor",
        basis_service_contract: "Service contract",
        basis_short_term_family_work: "Short-term family work",
        basis_student_work: "Student work",
        basis_traineeship: "Traineeship or apprenticeship",
        break_of: "break {minutes}",
        change_changed_at: "Changed {at}",
        change_moved: "From {previous} to {current}",
        change_reason_line: "Reason: {reason}",
        change_withdrawn: "The shift is withdrawn (it was {previous})",
        day_friday: "Fri",
        day_monday: "Mon",
        day_saturday: "Sat",
        day_sunday: "Sun",
        day_thursday: "Thu",
        day_tuesday: "Tue",
        day_wednesday: "Wed",
        decline: "I cannot",
        decline_failed: "The answer could not be recorded.",
        decline_hint: "The scheduler will see that you cannot and will arrange cover. The change stays on the list until that is settled.",
        decline_note: "Why can you not? (optional)",
        decline_send: "Send",
        decline_title: "I cannot work this",
        declined: "Recorded: you cannot work it.",
        duration_fixed_term: "Fixed term",
        duration_indefinite: "Indefinite",
        exempt_managing_person: "Managing person",
        exempt_other: "Another reason",
        free_day: "Off",
        load_failed: "This could not be read.",
        my_changes_title: "Changes you have not acknowledged: {count}",
        my_pending_title: "Requests waiting for a decision: {count}",
        my_rota: "My rota",
        my_rota_empty: "You have no shift rostered this week.",
        my_rota_empty_hint: "What you see here is published only. A supervisor's draft is not shown — until a week is published it is not a promise.",
        next_week: "Next week",
        no_swap_hint: "This portal has no shift swap and no cover request. If you cannot work a shift, press „I cannot“ on the change above — your manager sees it at once, with your reason. For planned time off, send a leave request.",
        previous_week: "Previous week",
        record_clock: "Clock stamps",
        record_manual: "Manual entry",
        record_pattern: "The work pattern",
        record_schedule: "The rota",
        state_cancelled: "Cancelled",
        state_draft: "Draft",
        state_published: "Published",
        this_week: "This week",
        time_full: "Full time",
        time_part: "Reduced time",
        today: "Today",
        try_again: "Try again",
        unit_hours: "h",
        unit_minutes: "min",
    },
    staff_absence: {
        days_recorded: "Days recorded: {count}",
        decided_by_other: "Somebody else decides your request — you cannot approve your own.",
        from: "From",
        kind: "Kind",
        kind_business_trip: "Business trip",
        kind_holiday: "Public holiday",
        kind_other: "Other",
        kind_sick_leave: "Sick leave",
        kind_training: "Training",
        kind_unexcused: "Unexcused absence",
        kind_vacation: "Leave",
        my_absences: "My absences",
        my_absences_hint: "Your requests and what was decided about them. Refusals stay visible — a refusal that disappears is a refusal that did not happen.",
        no_my_requests: "You have no requests for absence.",
        note: "Note",
        other_kinds_hint: "Only leave can be requested. Sick leave is reported to your manager, who enters it; a public holiday comes off the hotel's calendar; training and a business trip are the employer's own instruction. All of them appear in your record once they are entered.",
        other_kinds_title: "Other absences",
        range_backwards: "The end cannot be before the start.",
        range_too_long: "A request may cover at most {days} days. A longer absence is entered by a manager.",
        request_hint: "You may ask for leave. Sick leave is reported and recorded, a public holiday comes off the calendar, and training and business trips are entered by a manager.",
        request_leave: "Request leave",
        request_sent: "The request has been sent for a decision.",
        send_request: "Send request",
        source_calendar: "Holiday calendar",
        source_manager: "Entered by a manager",
        source_request: "Request",
        state_approved: "Approved",
        state_rejected: "Rejected",
        state_requested: "Requested",
        state_withdrawn: "Withdrawn",
        to: "To",
        total_absence: "Absence",
        withdraw: "Withdraw",
        withdrawn: "The request has been withdrawn.",
    },
    staff_document: {
        download: "Download",
        expired_on: "Expired {date}",
        expires_on: "Valid until {date}",
        kind_aneks: "Annexe",
        kind_drugo: "Other",
        kind_m1: "M-1 form",
        kind_nda: "Confidentiality undertaking",
        kind_odlok: "Decision",
        kind_pogodba: "Contract of employment",
        my_documents: "My documents",
        my_documents_hint: "What the house has filed about you. You read and download; the house files.",
        no_my_documents: "Nothing has been filed for you yet.",
        read_only_hint: "Documents cannot be added or removed here — the file is the employer's record. Only the annual-leave decision has a confirmation of receipt.",
    },
    staff_month: {
        badge_closed: "Closed",
    },
    staff_time: {
        break_counts_hint: "The break counts as working time and is never deducted.",
        break_short: "break {minutes}",
        correction_approved: "Approved",
        correction_date: "Date",
        correction_decided_by_other: "Somebody else decides — you cannot approve your own.",
        correction_hint: "You may ask; somebody else decides.",
        correction_kind: "Kind of stamp",
        correction_mode_existing: "A stamp that is there",
        correction_mode_missing: "A missing stamp",
        correction_no_stamps: "This day has no stamp that could be corrected.",
        correction_reason: "Reason",
        correction_rejected: "Rejected",
        correction_requested: "Requested",
        correction_send: "Send the request",
        correction_sent: "The request has been sent for a decision.",
        correction_time: "Time",
        correction_what: "What is being corrected",
        correction_which_stamp: "Which stamp",
        day_open: "The day is unfinished — no departure recorded.",
        decision_reason: "Reason: {reason}",
        excess_pending: "Excess — awaiting classification",
        first_in: "arrived {at}",
        kind_break_end: "End of break",
        kind_break_start: "Break",
        kind_clock_in: "Arrival",
        kind_clock_out: "Departure",
        last_out: "left {at}",
        month_pdf: "Monthly sheet (PDF)",
        my_corrections: "My correction requests",
        my_record: "My record",
        my_time: "My clock",
        next_month: "Next month",
        no_days: "No days recorded in this period.",
        of_which_break: "of which break {minutes}",
        open: "Unfinished",
        period_month: "Month",
        period_week: "Week",
        previous_month: "Previous month",
        queued: "Waiting for a connection: {count}",
        refused: "The stamp was not accepted.",
        not_recorded: "The stamp was not recorded: {reason}",
        request_correction: "Ask for a correction",
        stamped: "Recorded: {kind}",
        this_month: "This month",
        today_total: "Today",
        total_balance: "Balance",
        total_break: "Break",
        total_expected: "Contracted",
        total_worked: "Worked",
    },
    staff_vacation: {
        acknowledge: "I acknowledge",
        acknowledge_hint: "You are acknowledging RECEIPT of the decision, not agreement with the number of days.",
        acknowledged: "Acknowledged",
        acknowledged_toast: "Your acknowledgement has been recorded.",
        available: "Available",
        bucket_current: "This year",
        bucket_old: "Carried over",
        current_leave: "Annual leave {year}",
        days_count: "{count} days",
        decision_for_year: "Decision for {year}",
        decision_total: "Entitled: {days}",
        entitled: "Entitled",
        expired_days: "{days} lapsed — the deadline was {date}.",
        issued_at: "Issued by {who}, {at}",
        my_balance: "My leave",
        my_decisions: "My decisions",
        my_decisions_hint: "The yearly leave decisions the house has issued to you.",
        next_year: "Next year",
        no_balance: "There is no leave figure for this year.",
        no_balance_hint: "A balance appears once a decision has been issued and the person has a period of work in force.",
        no_my_decisions: "No decision has been issued to you yet.",
        no_usage: "No leave has been recorded for this year.",
        not_acknowledged: "Not acknowledged",
        not_issued_hint: "No decision has been issued for this year yet.",
        of_which_pending: "Of which awaiting a decision: {days}",
        of_which_used: "Of which used: {days}",
        old_leave: "Carried-over leave ({year})",
        over_remaining: "This is more than the {days} available. The decision is still the house’s.",
        part_base: "Base",
        part_children: "Children",
        part_education: "Education",
        part_full_year: "Total (full year)",
        part_responsibility: "Position of responsibility",
        part_special: "Special category",
        part_tenure: "Length of service",
        pdf: "PDF",
        pending: "Pending",
        previous_year: "Previous year",
        requested_days: "Working days requested: {days}",
        still_days: "{days} left",
        still_of: "{left} left of {total}",
        this_year: "This year",
        usage_history: "How it was used",
        use_by: "Use it by {date}.",
        used: "Used",
    },
    success: {
        add: "Successfully added",
        edit: "Successfully updated",
    },
    validation: {
        email: "Invalid email address",
        required: "This field is required",
    },
}
